import Close from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/Help';
import Autocomplete, { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip, { ChipPropsColorOverrides } from '@mui/material/Chip';
import { InputBaseProps } from '@mui/material/InputBase';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { OverridableStringUnion } from '@mui/types';
import { t } from 'i18next';
import { debounce, get } from 'lodash-es';
import React, {
  forwardRef,
  type Key,
  ReactElement,
  ReactNode,
  Ref,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { GeofenceStartAdornment } from '~pages/Settings/Administration/Sites/components/GeofenceStartAdornment';

import { SelectOption } from './Parts/SelectOption';

interface FormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  name: string;
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  sx?: SxProps<Theme>;
  clearable?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  chipsColor?: OverridableStringUnion<
    'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    ChipPropsColorOverrides
  >;
  defaultValue?: any;
  noOptionsText?: string | ReactNode;
  inputProps?: InputBaseProps['inputProps'];
  getValue: (value: any) => string; // Is used for the equal comparison of the values
  getInputValue?: (value: any) => string; // Is used for the value of the text input
  getLabel: (value: any) => string; // Is used for the label of the option
  groupBy?: (value: any) => string; // Is used for the groupBy of the option
  onInput?: (event: React.SyntheticEvent, value: string) => void;
  asyncCallback: (value: any) => any;
  debounceTime?: number;
  extraRequestOptions?: Record<string, any>;
  filterSelectedOptions?: boolean;
  limitTags?: number;
  onClear?: () => void;
  onSelect?: (value: any) => any;
  defaultSuggestions?: Record<string, any>[];
  hideLabel?: boolean;
  // Suppress the validation message text while keeping the error outline,
  // matching AutocompleteFormField's border-only error display.
  hideErrorMessage?: boolean;
  fetchOnInputChange?: boolean; // Set to false when this component is used with a non-typeahead api
  onMultipleOptionsChange?: <T>(values: T[]) => void;
  hideChipOverflow?: boolean;
  disablePortal?: boolean;
  prefilledValue?: string;
  dataUi?: string;
  listboxWidth?: string | number;
  wrapOptions?: boolean;
  renderOption?: (
    // MUI's getOptionProps folds `key` into the option props at runtime;
    // custom renderers must lift it onto their outermost element.
    props: React.HTMLAttributes<HTMLLIElement> & { key?: Key },
    option: any,
    state?: any,
  ) => React.ReactNode;
}

export interface AutocompleteAsyncFormFieldRef {
  reset: () => void;
  resetCursor: () => void;
  getSuggestions: () => Record<string, any>[];
  updateSuggestions: (newSuggestions: Record<string, any>[]) => void;
  refresh: () => void;
  getInputValue: () => string;
}

const AutocompleteAsyncFormFieldInner = <TFieldValues extends FieldValues = FieldValues>(
  {
    control,
    errors,
    name,
    label,
    clearable,
    getLabel,
    getValue,
    getInputValue,
    groupBy,
    chipsColor,
    isRequired,
    disabled = false,
    multiple = false,
    defaultValue,
    inputProps,
    sx,
    noOptionsText = t('form_fields.no_options') || '',
    asyncCallback,
    debounceTime = 400,
    extraRequestOptions = {},
    filterSelectedOptions,
    limitTags,
    onClear,
    onSelect,
    defaultSuggestions,
    onInput,
    hideLabel = false,
    hideErrorMessage = false,
    fetchOnInputChange = true,
    onMultipleOptionsChange,
    hideChipOverflow = false,
    disablePortal = true,
    placeholder,
    prefilledValue = '',
    dataUi,
    listboxWidth,
    wrapOptions = false,
    renderOption,
  }: FormFieldProps<TFieldValues>,
  ref: Ref<AutocompleteAsyncFormFieldRef>,
) => {
  const [inputValue, setInputValue] = useState(prefilledValue);
  const [suggestions, setSuggestions] = useState<Record<string, any>[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prevCursor, setPrevCursor] = useState<string>('');
  const [cursor, setCursor] = useState<string>('');
  const [allowFetch, setAllowFetch] = useState(true);
  const resetSuggestions = useCallback(() => {
    setAllowFetch(false);
    setSuggestions([]);
  }, []);
  const SCROLL_OFFSET = 300; // How far above the bottom of the scroll container to trigger a fetch
  const fetchSuggestions = async (
    query: string,
    cursor: string,
    extraRequestOptions: any,
  ) => {
    setIsLoading(true);
    const entities = await asyncCallback({
      ...extraRequestOptions, // I.e. items count, page number, etc.
      query,
      cursor,
    });

    if (entities?.data?.length) {
      setAllowFetch(true);
      setPrevCursor(cursor);
      setSuggestions((prevState) => {
        if (!prevCursor && !cursor) {
          return entities.data;
        } else {
          return [...prevState, ...entities.data];
        }
      });
      setCursor(entities.pagination?.after);
    } else if (entities?.length) {
      setAllowFetch(true);
      setSuggestions((prevState) => {
        if (inputValue) {
          return entities;
        } else {
          return [...prevState, ...entities];
        }
      });
    } else {
      resetSuggestions();
    }

    // if there is a prefilled value that matches only one suggestion,
    // automatically set the input value to that suggestion
    if (prefilledValue && suggestions.length === 1) {
      setFirstValue(suggestions[0]);
    }

    setIsLoading(false);
  };
  const searchDelayed = useMemo(
    () => debounce(fetchSuggestions, debounceTime),
    [asyncCallback],
  );
  const handleInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (prefilledValue && reason === 'reset' && value === '') {
      // use prefilledValue the default when resetting to empty
      setInputValue(prefilledValue);
    } else {
      setInputValue(value);
    }

    if (onInput) {
      onInput(event, value);
    }

    if (reason === 'input' && fetchOnInputChange) {
      if (value === '' && defaultSuggestions) {
        searchDelayed('', '', extraRequestOptions);
      } else {
        searchDelayed(value, '', extraRequestOptions);
      }
    }
    if (reason === 'clear' || reason === 'reset') {
      if (defaultSuggestions) {
        setSuggestions(defaultSuggestions);
      } else if (isOpen) {
        if (prefilledValue === inputValue) {
          searchDelayed(prefilledValue, '', extraRequestOptions);
        } else {
          searchDelayed('', '', extraRequestOptions);
        }
      }
    }
  };
  const handleClose = () => {
    searchDelayed.cancel();
    setIsOpen(false);
    setIsLoading(false);
    setPrevCursor('');
    setCursor('');
    setSuggestions([]);
    setAllowFetch(true);
  };

  const updateSuggestions = useCallback(
    (newSuggestions: Record<string, any>[]) => {
      setSuggestions(newSuggestions);
    },
    [setSuggestions],
  );
  useImperativeHandle(
    ref,
    () => ({
      reset() {
        // If we indicate that this component is reset, we want set the suggestions
        // and setAllowFetch to their initial values.
        // setAllowFetch allows for additional fetches when the suggestions are empty, so on
        // this component reset, we want to fetch one more time.
        setAllowFetch(true);
        setSuggestions([]);
      },
      resetCursor() {
        setPrevCursor('');
        setCursor('');
      },
      getSuggestions() {
        return suggestions;
      },
      updateSuggestions(newSuggestions: Record<string, any>[]) {
        updateSuggestions(newSuggestions);
      },
      refresh() {
        fetchSuggestions('', '', {});
      },
      getInputValue() {
        return inputValue;
      },
    }),
    [suggestions],
  );

  //Initial fetch of data
  useEffect(() => {
    if (isOpen && !isLoading && !suggestions.length && allowFetch) {
      if (prefilledValue && prefilledValue === inputValue) {
        fetchSuggestions(prefilledValue, '', extraRequestOptions);
      } else {
        fetchSuggestions('', cursor, extraRequestOptions);
      }
    }
  }, [isOpen, isLoading, inputValue, prefilledValue, allowFetch]);

  useEffect(() => {
    if (prefilledValue) {
      setInputValue(prefilledValue);
    }
  }, [prefilledValue]);

  const [firstValue, setFirstValue] = useState<any>(defaultValue);

  // fetch suggestions when the prefilled value is changed
  useEffect(() => {
    if (prefilledValue && prefilledValue === inputValue) {
      fetchSuggestions(prefilledValue, '', extraRequestOptions);
    }
  }, [prefilledValue, inputValue, suggestions.length]);

  const fieldErrorMessage = get(errors, `${name}.message`) as string | undefined;

  return (
    <>
      <Controller
        control={control}
        name={name as FieldPath<TFieldValues>}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            ref={ref}
            open={isOpen}
            disablePortal={disablePortal}
            onOpen={() => {
              setIsOpen(true);
              if (defaultSuggestions) {
                setSuggestions(defaultSuggestions);
              }
            }}
            onClose={handleClose}
            noOptionsText={
              <Box
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                {noOptionsText}
              </Box>
            }
            sx={{ ...sx }}
            slotProps={{
              listbox: {
                onScroll: (event: React.UIEvent<HTMLUListElement>) => {
                  const target = event.currentTarget as HTMLElement;
                  if (
                    !!cursor &&
                    !isLoading &&
                    target.scrollTop + target.clientHeight >=
                      target.scrollHeight - SCROLL_OFFSET
                  ) {
                    // Exclude input value on subsequent fetches to avoid returning an empty array
                    fetchSuggestions('', cursor, extraRequestOptions);
                  }
                },
              },
              paper: { elevation: 4, sx: { width: listboxWidth ?? 'auto' } },
            }}
            multiple={multiple}
            options={suggestions}
            filterSelectedOptions={filterSelectedOptions ?? !!multiple}
            disableCloseOnSelect={multiple}
            limitTags={limitTags}
            includeInputInList
            fullWidth
            disabled={disabled}
            defaultValue={defaultValue}
            onInputChange={(event, value, reason) => {
              if (prefilledValue && firstValue && reason === 'reset') {
                if (multiple) {
                  onChange([firstValue]);
                  setInputValue('');
                  return;
                } else {
                  onChange(firstValue);
                }
              }
              handleInputChange(event, value, reason);
            }}
            inputValue={inputValue}
            filterOptions={(options) => {
              if (!fetchOnInputChange) {
                return options.filter((item) =>
                  getLabel(item).toLowerCase().includes(inputValue.toLowerCase()),
                );
              } else {
                return options;
              }
            }}
            onChange={async (event, values, reason) => {
              if (reason === 'clear') {
                if (onClear) {
                  onClear();
                } else {
                  onChange(null);
                }
              } else {
                let formatedValues = values;
                if (onSelect) {
                  formatedValues = await onSelect(values);
                }
                if (multiple && onMultipleOptionsChange) {
                  onMultipleOptionsChange(formatedValues);
                }
                if (multiple && prefilledValue && formatedValues.length) {
                  setInputValue('');
                }
                onChange(formatedValues);
              }
            }}
            renderGroup={(params) => {
              return (
                <Box component="li" key={params.key}>
                  <Box sx={(theme) => ({ bgcolor: theme.brandV2.colors.treadGray8 })}>
                    <Typography sx={{ pl: 1, fontStyle: 'italic', fontWeight: 500 }}>
                      {params.group}
                    </Typography>
                  </Box>

                  {/* Options are <li> elements, so their container must be a list —
                      mirrors MUI's default group markup (li > ul.MuiAutocomplete-groupUl
                      > li); a plain div here is invalid DOM nesting. */}
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {params.children}
                  </Box>
                </Box>
              );
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              const inputValue = getInputValue?.(option);
              if (inputValue) {
                return inputValue;
              }
              return getLabel(option);
            }}
            isOptionEqualToValue={(option, value) => getValue(option) === getValue(value)}
            groupBy={groupBy}
            loading={isLoading}
            renderValue={
              multiple
                ? (selectedValue, getItemProps) =>
                    Array.isArray(selectedValue) ? (
                      <>
                        {selectedValue.map((option: string, index: number) => (
                          <Chip
                            size="small"
                            color={chipsColor || 'primary'}
                            label={getLabel(option)}
                            deleteIcon={<Close />}
                            style={{
                              ...(hideChipOverflow &&
                                limitTags &&
                                selectedValue.length > limitTags && { maxWidth: '70%' }),
                              ...(limitTags && index >= limitTags && { display: 'none' }),
                            }}
                            {...getItemProps({ index })}
                            key={index}
                          />
                        ))}
                        {limitTags && selectedValue.length > limitTags && (
                          <Typography>+{selectedValue.length - limitTags}</Typography>
                        )}
                      </>
                    ) : null
                : undefined
            }
            renderInput={(params) => (
              <>
                {!hideLabel && (
                  <FormFieldLabel
                    label={`${label}${!!label && isRequired ? ' *' : ''}`}
                  />
                )}
                <TextField
                  {...params}
                  label=""
                  size="small"
                  sx={{ display: 'flex' }}
                  margin="dense"
                  variant="outlined"
                  error={!!get(errors, name)}
                  helperText={
                    (hideErrorMessage ? undefined : fieldErrorMessage) ??
                    (!value?.id && !value?.length && prefilledValue && (
                      <Box
                        sx={{
                          display: 'flex',
                          fontSize: 12,
                          gap: 0.5,
                        }}
                      >
                        <HelpIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2">
                          {t('form_validation_errors.please_confirm_value')}
                        </Typography>
                      </Box>
                    ))
                  }
                  className={`${isRequired && !disabled ? 'required' : ''} ${
                    prefilledValue && !value?.id && !disabled && !value?.length
                      ? 'prefilled'
                      : ''
                  }`.trim()}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      ...inputProps,
                      autoComplete: 'off',
                      'data-1p-ignore': 'true', // Special attribute to prevent 1Password autocomplete
                      'data-test-id': get(
                        inputProps,
                        'data-test-id',
                        'async-autocomplete',
                      ),
                      'aria-label': hideLabel ? label : undefined,
                      ...(dataUi && { 'data-ui': dataUi }),
                    },
                    input: {
                      ...params.InputProps,
                      ...((name === 'pickUpAddress' ||
                        name === 'dropOffAddress' ||
                        name === 'stagingAddress') && {
                        startAdornment: (
                          <GeofenceStartAdornment
                            geofenceType={value?.geofence?.geofenceType}
                          />
                        ),
                      }),
                      placeholder: placeholder,
                    },
                  }}
                />
              </>
            )}
            value={value}
            disableClearable={!clearable}
            renderOption={
              renderOption ||
              ((props, option, { inputValue, selected }) => {
                // MUI v6 folds `key` into the option props; it must land on the
                // outermost element renderOption returns (SelectOption), not on
                // the inner <li> the spread reaches — otherwise React's
                // unique-key warning fires for the options list.
                const { key, ...optionProps } = props;

                return (
                  <SelectOption
                    key={key as Key}
                    isCheckEnabled={multiple}
                    selected={selected}
                    option={option}
                    getLabel={getLabel}
                    inputValue={inputValue}
                    props={optionProps}
                    onDelete={option.onDelete}
                    wrapLabel={wrapOptions}
                    showLinkIcon={!!option.companyShare}
                  />
                );
              })
            }
          />
        )}
      />
    </>
  );
};

const AutocompleteAsyncFormField = forwardRef(AutocompleteAsyncFormFieldInner) as <
  TFieldValues extends FieldValues = FieldValues,
>(
  props: FormFieldProps<TFieldValues> & RefAttributes<AutocompleteAsyncFormFieldRef>,
) => ReactElement;

export { AutocompleteAsyncFormField };
